import get from 'lodash.get';
import set from 'lodash.set';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import * as PostagePurchaseActions from '../../../store/postage-purchase/postage-purchase.actions';
import { baseReducer as BaseReducer } from '../../../store/core/base-reducer';
import { CostReviewHelperService } from '../../../services/cost-review-helper-service';

import * as Actions from './batches.purchase.actions';
import { calculateNextCostReviewState } from '../../cost-review/wallet-costs-helper';

const initialState = {
  jobIdsToFpSetIdMap: emptyObject,
  fpSetIdToNotoIdMap: emptyObject,
  shipDate: undefined,
  lbsModalId: '',
};

const updateMaps = (state, { jobIdsToFpSetIdMap }) => ({
  ...state,
  jobIdsToFpSetIdMap: {
    ...state.jobIdsToFpSetIdMap,
    ...jobIdsToFpSetIdMap,
  },
});

const updateWalletPurchaseAmount = (state, fulfillmentPlanSetId, walletProviderCost, amount) => {
  const costReviewSummary = state?.[fulfillmentPlanSetId]?.costReviewSummary;

  if (!costReviewSummary) {
    return state;
  }

  return {
    ...state,
    [fulfillmentPlanSetId]: {
      ...state[fulfillmentPlanSetId],
      costReviewSummary: calculateNextCostReviewState(costReviewSummary, walletProviderCost.walletId, amount),
    },
  };
};

const updateInsuranceOrProviderAmount = (state, fulfillmentPlanSetId, provider, amount) => {
  const costReviewSummary = get(state, `${fulfillmentPlanSetId}.costReviewSummary`);

  if (!costReviewSummary) {
    return state;
  }

  const insuranceCosts = get(costReviewSummary, 'insuranceCosts', emptyArray);
  const providerCosts = get(costReviewSummary, 'providerCosts', emptyArray);
  const providerToUpdate = providerCosts.find((prov) => prov.sellerLabelProviderId === get(provider, 'sellerLabelProviderId'));
  set(providerToUpdate, 'purchaseAmount', amount);
  const insuranceToUpdate = insuranceCosts.find((prov) => prov.insuranceProviderId === get(provider, 'insuranceProviderId'));
  set(insuranceToUpdate, 'purchaseAmount', amount);

  return {
    ...state,
    [fulfillmentPlanSetId]: {
      ...state[fulfillmentPlanSetId],
      costReviewSummary: {
        ...state[fulfillmentPlanSetId].costReviewSummary,
        insuranceCosts,
        providerCosts,
      },
    },
  };
};

const setSellerProviderError = (state, action) => {
  const fulfillmentPlanSetId = get(action, 'jobGuid');
  const sellerLabelProviderId = get(action, 'sellerLabelProviderId');
  const error = get(action, 'error');
  const job = state[fulfillmentPlanSetId];
  if (!job) {
    return {
      ...state,
    };
  }
  const { providerCosts } = job.costReviewSummary;
  const adjustedProviders = providerCosts.map((provider) => ({
    ...provider,
    providerErrorMessage: provider.sellerLabelProviderId === sellerLabelProviderId ? error : '',
  }));
  return {
    ...state,
    purchasePostageLoading: false,
    [fulfillmentPlanSetId]: {
      ...state[fulfillmentPlanSetId],
      costReviewSummary: {
        ...state[fulfillmentPlanSetId].costReviewSummary,
        providerCosts: adjustedProviders,
      },
    },
  };
};

const cleanMap = (map, fulfillmentPlanSetId) => {
  const cleanedMap = {};

  Object.keys(map).forEach((key) => {
    if (map[key] === fulfillmentPlanSetId) {
      return;
    }
    cleanedMap[key] = map[key];
  });
  return cleanedMap;
};

const actionHandlerMap = {
  [Actions.VALIDATE_BATCHES_SUCCESS]: updateMaps,
  [Actions.COST_REVIEW_BATCHES_SUCCESS]: updateMaps,
  [Actions.COST_REVIEW_BATCHES_REQUEST]: (state, { fulfillmentPlanSetId }) => {
    return {
      ...state,
      [fulfillmentPlanSetId]: {
        ...state[fulfillmentPlanSetId],
        isLoadingCostReview: true,
      },
    };
  },
  [Actions.BATCHES_OPEN_COST_REVIEW]: (state, { fulfillmentPlanSetId, costReviewSummary, recommendationSummary, appliedSavings }) => {
    cleanMap(state.jobIdsToFpSetIdMap, fulfillmentPlanSetId);
    return {
      ...state,
      [fulfillmentPlanSetId]: {
        ...get(state, fulfillmentPlanSetId, emptyObject),
        costReviewSummary: CostReviewHelperService.mergeWalletProviders(costReviewSummary),
        recommendationSummary,
        appliedSavings,
        isLoadingCostReview: false,
      },
      shipDate: undefined,
    };
  },
  [PostagePurchaseActions.POSTAGE_PURCHASE_REQUEST_FAILURE]: (state, action) => {
    return setSellerProviderError(state, action);
  },
  [Actions.BATCHES_PURCHASE_WALLET_PURCHASE_AMOUNT_CHANGED]: (state, { fulfillmentPlanSetId, walletProviderCost, amount }) => {
    return updateWalletPurchaseAmount(state, fulfillmentPlanSetId, walletProviderCost, amount);
  },
  [Actions.BATCHES_PURCHASE_PROVIDER_PURCHASE_AMOUNT_CHANGED]: (state, { fulfillmentPlanSetId, provider, amount }) => {
    return updateInsuranceOrProviderAmount(state, fulfillmentPlanSetId, provider, amount);
  },
  [Actions.BATCHES_PURCHASE_INSURANCE_PURCHASE_AMOUNT_CHANGED]: (state, { fulfillmentPlanSetId, provider, amount }) => {
    return updateInsuranceOrProviderAmount(state, fulfillmentPlanSetId, provider, amount);
  },
  [Actions.CREATE_LABELS_FOR_BATCHES_SUCCESS]: updateMaps,
  [Actions.BATCHES_CLEANUP_BATCH_DATA]: (state, { fulfillmentPlanSetId }) => {
    return {
      ...state,
      [fulfillmentPlanSetId]: undefined,
    };
  },
  [Actions.BATCHES_PURCHASE_SET_SHIP_DATE]: (state, { shipDate }) => {
    return {
      ...state,
      shipDate,
    };
  },
  [Actions.SET_BATCHES_LBS_MODAL_ID]: (state, { lbsModalId }) => {
    return {
      ...state,
      lbsModalId,
    };
  },
  [Actions.BATCH_COST_REVIEW_APPLY_RECOMMENDATIONS]: (state, { fulfillmentPlanSetId }) => {
    return {
      ...state,
      [fulfillmentPlanSetId]: {
        ...state[fulfillmentPlanSetId],
        isApplyingRecommendations: true,
      },
    };
  },
  [Actions.BATCH_COST_REVIEW_APPLY_RECOMMENDATIONS_SUCCESS]: (state, { fulfillmentPlanSetId }) => {
    return {
      ...state,
      [fulfillmentPlanSetId]: {
        ...state[fulfillmentPlanSetId],
        isApplyingRecommendations: false,
      },
    };
  },
  [Actions.BATCH_COST_REVIEW_APPLY_RECOMMENDATIONS_ERROR]: (state, { fulfillmentPlanSetId }) => {
    return {
      ...state,
      [fulfillmentPlanSetId]: {
        ...state[fulfillmentPlanSetId],
        isApplyingRecommendations: false,
      },
    };
  },
};

export const ConnectedActionHandlerMap = BaseReducer(initialState, actionHandlerMap);

export { namespace } from './batches.purchase.reducer.metadata';
